// src/utils/flyToCart.js
// ─────────────────────────────────────────────────────────────
// "Add to Cart" flying animation.
// Clones the product image, animates it flying from where the
// user clicked to the header cart icon, then pulses the cart
// count badge. No toast needed — the animation IS the feedback.
// ─────────────────────────────────────────────────────────────

/**
 * @param {HTMLElement} sourceEl - the element to fly (usually the product image,
 *   or the clicked "Add to Cart" button if no image is available).
 */
export const flyToCart = (sourceEl) => {
  try {
    if (!sourceEl) return;

    const cartTarget =
      document.querySelector(".header-cart-icon") ||
      document.querySelector(".cartcount")?.closest("a, button, div") ||
      document.querySelector(".cartcount");

    if (!cartTarget) return;

    const startRect = sourceEl.getBoundingClientRect();
    const endRect = cartTarget.getBoundingClientRect();

    // Prefer flying the actual <img> if the source contains one
    const imgEl = sourceEl.tagName === "IMG" ? sourceEl : sourceEl.querySelector("img");

    const flyer = document.createElement("div");
    flyer.className = "fly-to-cart-ghost";

    if (imgEl && imgEl.src) {
      const img = document.createElement("img");
      img.src = imgEl.src;
      flyer.appendChild(img);
    } else {
      flyer.classList.add("fly-to-cart-ghost--dot");
    }

    const size = 56;
    flyer.style.position = "fixed";
    flyer.style.left = `${startRect.left + startRect.width / 2 - size / 2}px`;
    flyer.style.top = `${startRect.top + startRect.height / 2 - size / 2}px`;
    flyer.style.width = `${size}px`;
    flyer.style.height = `${size}px`;
    flyer.style.zIndex = "9999";
    flyer.style.pointerEvents = "none";
    flyer.style.borderRadius = "50%";
    flyer.style.overflow = "hidden";
    flyer.style.background = "#fff";
    flyer.style.boxShadow = "0 6px 20px rgba(0,0,0,.18)";
    flyer.style.transition = "transform .7s cubic-bezier(.35,0,.25,1), opacity .7s ease";
    flyer.style.opacity = "1";

    document.body.appendChild(flyer);

    const dx = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
    const dy = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);

    // Kick off animation on next frame
    requestAnimationFrame(() => {
      flyer.style.transform = `translate(${dx}px, ${dy}px) scale(.15)`;
      flyer.style.opacity = "0.3";
    });

    const cleanup = () => {
      flyer.remove();
      // pulse the cart icon / count
      cartTarget.classList.add("cart-bump");
      setTimeout(() => cartTarget.classList.remove("cart-bump"), 420);
    };

    flyer.addEventListener("transitionend", cleanup, { once: true });
    // Safety net in case transitionend doesn't fire
    setTimeout(cleanup, 800);
  } catch (e) {
    // animation is purely cosmetic — never let it break add-to-cart
    console.error("flyToCart animation error:", e);
  }
};
